import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, InputBase } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

interface CartQuantityStepperProps {
  quantity: number;
  disabled?: boolean;
  increaseDisabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  onChange: (quantity: number) => void;
  decreaseLabel?: string;
  increaseLabel?: string;
  quantityLabel?: string;
}

const formatQty = (value: number) => {
  if (!Number.isFinite(value)) return '';
  return String(parseFloat(value.toPrecision(12)));
};

const parseQty = (raw: string): number | null => {
  const normalized = raw.trim().replace(',', '.');
  if (!normalized) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value) || value < 0) return null;
  return value;
};

const CartQuantityStepper: React.FC<CartQuantityStepperProps> = ({
  quantity,
  disabled = false,
  increaseDisabled = false,
  size = 'medium',
  fullWidth = false,
  onChange,
  decreaseLabel = 'Disminuir cantidad',
  increaseLabel = 'Aumentar cantidad',
  quantityLabel = 'Cantidad',
}) => {
  const iconFontSize = size === 'small' ? 16 : 18;
  const focusedRef = useRef(false);
  const [draft, setDraft] = useState(formatQty(quantity));

  useEffect(() => {
    if (!focusedRef.current) setDraft(formatQty(quantity));
  }, [quantity]);

  const commit = (next: number) => {
    const normalized = parseFloat(next.toPrecision(12));
    if (increaseDisabled && normalized > quantity) {
      setDraft(formatQty(quantity));
      return;
    }
    setDraft(formatQty(normalized));
    if (normalized === quantity) return;
    onChange(normalized);
  };

  const commitValue = (raw: string) => {
    const parsed = parseQty(raw);
    if (parsed == null) {
      setDraft(formatQty(quantity));
      return;
    }
    commit(parsed);
  };

  const stepBy = (delta: number) => {
    const parsed = parseQty(draft);
    const base = parsed ?? quantity;
    commit(Math.max(0, base + delta));
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        ...(fullWidth
          ? {
              width: '100%',
              justifyContent: 'space-between',
            }
          : null),
      }}
    >
      <IconButton
        size="small"
        aria-label={decreaseLabel}
        disabled={disabled || quantity <= 0}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => stepBy(-1)}
        sx={{ borderRadius: 0, px: size === 'small' ? 0.5 : 0.75 }}
      >
        <RemoveIcon sx={{ fontSize: iconFontSize }} />
      </IconButton>
      <InputBase
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => {
          focusedRef.current = true;
          e.target.select();
        }}
        onBlur={(e) => {
          focusedRef.current = false;
          commitValue(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          }
          if (e.key === 'Escape') {
            setDraft(formatQty(quantity));
            (e.target as HTMLInputElement).blur();
          }
        }}
        inputProps={{
          'aria-label': quantityLabel,
          inputMode: 'decimal',
        }}
        sx={{
          width: fullWidth ? '100%' : size === 'small' ? 44 : 56,
          flex: fullWidth ? 1 : undefined,
          '& input': {
            textAlign: 'center',
            fontWeight: 600,
            fontSize: size === 'small' ? 13 : 14,
            py: 0.5,
            px: 0.25,
          },
        }}
      />
      <IconButton
        size="small"
        aria-label={increaseLabel}
        disabled={disabled || increaseDisabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => stepBy(1)}
        sx={{ borderRadius: 0, px: size === 'small' ? 0.5 : 0.75 }}
      >
        <AddIcon sx={{ fontSize: iconFontSize }} />
      </IconButton>
    </Box>
  );
};

export default CartQuantityStepper;
